import type { ProductComparisonFeatureItem } from '../../../models/features/main/ProductComparisonFeatureItem';
import { FeatureState } from '../../../store/featureState';
import type { AppDispatch, RootState } from '../../../store/store';
import { apiClient } from '../../ApiClient';
import { dispatchEvent } from '../../EventService';
import {
    getNewFeatureUuid,
    getDefaultBackableFeatureItem,
    isProductComparisonFeatureItem,
    defaultBackToPreviousFeatureItems
} from '../../FeatureService';
import type { FeatureHandler } from '../FeatureHandler';

export const productComparisonMainFeatureHandler = {
    async startFeatureItem(
        dispatch: AppDispatch,
        state: RootState
    ): Promise<void> {
        const { selectedProductIds, selectedProducts, loadingProductIds } =
            state.productComparisonState;
        if (
            selectedProductIds.length < 2 ||
            selectedProductIds.length !== selectedProducts.length ||
            loadingProductIds.length > 0
        ) {
            return;
        }
        dispatchEvent({
            type: 'bri.open_comparison',
            isInteraction: true,
            productIds: selectedProductIds
        });
        const newMainFeatureItem: ProductComparisonFeatureItem = {
            featureUuid: getNewFeatureUuid('product-comparison'),
            featureType: 'product-comparison',
            loadingStatus: 'loading',
            comparingProductIds: selectedProductIds,
            comparingProducts: selectedProducts,
            table: null,
            ...getDefaultBackableFeatureItem(state)
        };
        dispatch.featureState.setMenuFeatureItem(null);
        dispatch.featureState.setMainFeatureItem(newMainFeatureItem);
        dispatch.uiState.setMode('full');
        await dispatch.featureState.startFeature({
            featureType: 'chat',
            chatType: 'product-comparison'
        });
        try {
            const table = await apiClient.assistant.productComparisonTable({
                body: {
                    productIds: newMainFeatureItem.comparingProductIds,
                    fillWithSimilarUntil: 3
                }
            });
            dispatch.featureState.updateMainFeatureItem({
                ...newMainFeatureItem,
                loadingStatus: 'success',
                table
            });
            await dispatch.featureState.updateContext();
            dispatch.featureState.setSidebarExpanded(true);
            dispatch.productComparisonState.setSelectedProducts([]);
        } catch {
            dispatch.featureState.updateMainFeatureItem({
                ...newMainFeatureItem,
                loadingStatus: 'error'
            });
            await dispatch.featureState.updateContext();
        }
    },
    removeProduct(
        state: Readonly<FeatureState>,
        productId: string
    ): FeatureState {
        if (!isProductComparisonFeatureItem(state.mainFeatureItem)) {
            return state;
        }
        const newMainFeatureItem: ProductComparisonFeatureItem = {
            ...state.mainFeatureItem,
            comparingProductIds:
                state.mainFeatureItem.comparingProductIds.filter(
                    id => id !== productId
                ),
            comparingProducts: state.mainFeatureItem.comparingProducts.filter(
                p => p.productId !== productId
            ),
            ...(state.mainFeatureItem.table && {
                table: {
                    ...state.mainFeatureItem.table,
                    products: state.mainFeatureItem.table.products.filter(
                        p => p.productId !== productId
                    )
                }
            })
        };
        return {
            ...state,
            mainFeatureItem: newMainFeatureItem
        };
    },
    isBackable: true,
    backToPreviousFeatureItems(dispatch: AppDispatch, state: RootState): void {
        if (
            isProductComparisonFeatureItem(
                state.featureState.mainFeatureItem
            ) &&
            state.featureState.mainFeatureItem.table
        ) {
            dispatch.productComparisonState.setSelectedProducts(
                state.featureState.mainFeatureItem.table.products
            );
        }
        defaultBackToPreviousFeatureItems(dispatch, state);
    },
    isRestorable: true
} as const satisfies FeatureHandler<ProductComparisonFeatureItem>;
